import dayjs, { dateDisplayFormat } from '~libs/dayjs';

import { Column } from '~components/table-view';

/**
 * Column definitions for the Services Australia budget import result tables (TAV-3168).
 *
 * They live here rather than inline in ImportBudgetsModal so the deferred "review before import"
 * step can render the same rows through the same columns without redefining them. Precedent:
 * CLAIM_TABLE_COLUMNS in SubmitClaimsModal, one array serving a confirm preview and two result
 * tables.
 */

// Item dates arrive as ISO datetime strings off the written row, so they always need formatting.
// Null is defensive only: a budget without both dates cannot be stored and is reported as skipped.
const formatBoundary = (value: unknown): string =>
  value ? dayjs(value as string).format(dateDisplayFormat) : 'Not set';

// Created / Updated: budgets the import actually wrote.
export const importItemColumns: Column[] = [
  {
    id: 'client_name',
    header: 'Client',
    type: 'text',
  },
  {
    id: 'budget_type',
    header: 'Budget type',
    type: 'text',
  },
  {
    id: 'period',
    header: 'Period',
    type: 'text',
    deriveValue: (row) => `${formatBoundary(row.start_date)} - ${formatBoundary(row.end_date)}`,
  },
];

// Skipped: budgets the import fetched but could not write.
export const importSkippedColumns: Column[] = [
  {
    id: 'client_name',
    header: 'Client',
    type: 'text',
    // A skipped budget usually has no matching client, so name the care recipient Services Australia
    // sent instead. That number is what a provider searches on to work out who was missed.
    deriveValue: (row) => {
      if (row.client_name) return row.client_name;
      if (row.care_recipient_id) return `Care recipient ${row.care_recipient_id}`;
      return 'Unmatched';
    },
  },
  {
    id: 'sah_budget_id',
    header: 'Budget',
    type: 'text',
    // Without this the skipped list says something was missed but not WHICH budget, which is the one
    // thing a provider needs to look it up in Services Australia. Two budgets can share a client and
    // a type and differ only by id, so the other columns do not identify a row on their own.
    deriveValue: (row) => row.sah_budget_id ?? 'Unknown',
  },
  {
    id: 'budget_type',
    header: 'Budget type',
    type: 'text',
    // The raw Services Australia code, not a TurnPoint funding type name: an unrecognised budget
    // type is itself one of the skip reasons, so there may be nothing to resolve it against.
    deriveValue: (row) => row.budget_type ?? 'Unknown',
  },
  {
    id: 'reason',
    header: 'Reason',
    type: 'text',
  },
];
